import type { Theme } from '@material-ui/core'
import { makeStyles } from '@material-ui/core'
import moment from 'moment-timezone'
import React from 'react'
import type { EventProps } from 'react-big-calendar'
import { useCalendar } from '../CalendarContext'
import type { CalendarEvent } from 'paintscout'
import classnames from 'classnames'
import { useDialogs, Tooltip, useClientOptions } from '@paintscout/ui'
import { EventDialog, ViewEventsDialog } from '../../../dialogs'
import { USER_COLOR_MAP } from '@paintscout/util'
import { getTimezone, translateEventType } from '@paintscout/util/builder'

const useStyles = makeStyles<Theme, { color: string }>((theme) => ({
  root: {
    display: 'flex',
    backgroundColor: ({ color }) => USER_COLOR_MAP[color]?.light || `${color}50`,
    borderRadius: theme.borderRadius.md,
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(11),
    height: '20px',
    position: 'relative',
    zIndex: 1000
  },
  timeWrapper: {
    backgroundColor: ({ color }) => color,
    borderRadius: theme.borderRadius.md,
    paddingRight: '5px',
    minWidth: '65px',
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    height: '100%',
    zIndex: 2
  },
  eventType: {
    color: ({ color }) => USER_COLOR_MAP[color]?.dark || theme.palette.text.secondary,
    paddingLeft: theme.spacing(1),
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  grouped: {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    position: 'relative',
    zIndex: 1000
  },
  estimatorName: {
    color: ({ color }) => USER_COLOR_MAP[color]?.dark || theme.palette.text.secondary,
    paddingLeft: theme.spacing(1),
    width: 'calc(100% - 16px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  eventCount: {
    width: 'fit-content',
    minWidth: '18px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.palette.common.white,
    backgroundColor: ({ color }) => color,
    borderRadius: theme.borderRadius.md,
    fontWeight: 500,
    height: '100%'
  },
  integrationOverlay: {
    position: 'relative',
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      zIndex: 1,
      borderRadius: theme.borderRadius.md,
      backgroundImage: `
        repeating-linear-gradient(
          135deg,
          rgba(0,0,0,0.04),
          rgba(0,0,0,0.04) 3px,
          transparent 3px,
          transparent 12px
        )
      `
    }
  }
}))

export default function MonthEvent(props: EventProps<CalendarEvent>) {
  const { event } = props

  // below: AKA if only one estimator is selected
  if (event.subsequentEvents === undefined) {
    return <SingleMonthEvent {...props} />
  } else {
    return <GroupedMonthEvent {...props} />
  }
}

function SingleMonthEvent(props: EventProps<CalendarEvent>) {
  const { event } = props
  const { options } = useClientOptions()
  const timezone = getTimezone({ options })
  const color = event.estimator.color
  const classes = useStyles({ color })
  const { updateEvent, deleteEvent } = useCalendar()
  const { openDialog, dismissDialog } = useDialogs()

  const handleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()

    openDialog(EventDialog, {
      event,
      onDelete: deleteEvent,
      onConfirm: (updatedEvent: CalendarEvent, eventTemplate: string) => {
        updateEvent(updatedEvent, eventTemplate)
        dismissDialog()
      }
    })
  }

  if (event.eventType === '_google-calendar_') {
    const transparency = event.transparency === 'transparent' ? 'Free' : 'Busy'
    return (
      <Tooltip content={`Imported from Google (${transparency})`} hideIcon disableWrapper>
        <div className={classnames(classes.root, classes.integrationOverlay, 'rbc-event-imported')}>
          <div className={classes.timeWrapper}>{moment(event.start).tz(timezone).format('h:mma')}</div>
          <div className={classes.eventType}>
            <p style={{ position: 'relative', zIndex: 2 }}>Event</p>
          </div>
        </div>
      </Tooltip>
    )
  }

  return (
    <div className={classes.root} onClick={handleClick} onTouchStart={handleClick} onMouseDown={handleClick}>
      <div className={classes.timeWrapper}>{moment(event.start).tz(timezone).format('h:mma')}</div>
      <div className={classes.eventType}>{translateEventType({ eventType: event.eventType, options })}</div>
    </div>
  )
}

function GroupedMonthEvent(props: EventProps<CalendarEvent>) {
  const { event } = props
  const color = event.estimator.color
  const classes = useStyles({ color })
  const { addEvent, updateEvent, deleteEvent, selectedUsers } = useCalendar()
  const { openDialog } = useDialogs()

  const handleClick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    openDialog(ViewEventsDialog, {
      events: [event, ...event.subsequentEvents],
      addEvent,
      updateEvent,
      deleteEvent,
      selectedUsers
    })
  }

  return (
    <div
      className={classnames(classes.grouped, classes.root)}
      onClick={handleClick}
      onTouchStart={handleClick}
      onMouseDown={handleClick}
    >
      <div className={classes.estimatorName}>{event.estimator.name}</div>
      <div className={classes.eventCount}>{event.subsequentEvents?.length + 1}</div>
    </div>
  )
}
